import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  useTheme,
  useMediaQuery,
  Chip,
  Button,
  InputAdornment,
  FormControl,
  MenuItem,
  Select,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import UserLayout from "components/layouts/UserLayout";
import CustomTextField from "components/mui/text-field";
import { useNavigate } from "react-router-dom";
import Icon from "components/icon";
import "assets/CSS/Switch.css";

const EmployeeSync = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [syncEnabled, setSyncEnabled] = useState(true);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Top cards data
  const topCardsData = [
    {
      id: 1,
      value: "12",
      label: "Total Employees",
      icon: "solar:users-group-rounded-outline",
       iconColor: "#23A26D",
      bgColor: "#E8F5E9",
      
    },
    {
      id: 2,
      value: "28",
      label: "Synced",
      icon: "material-symbols:sync-outline",
     iconColor: "#003A70",
      bgColor: "#f0f0f1",
    },
    {
      id: 3,
      value: "8",
      label: "Conflicts",
      icon: "codicon:error",
      iconColor: "#DD992F",
      bgColor: "#FFF3E0",
    },
    {
      id: 4,
      value: "2",
      label: "Total Errors",
      icon: "bx:error",
      iconColor: "#d32f2f",
      bgColor: "#ffebee",
    },
  ];

  // Employee data
  const employees = [
    {
      id: 1,
      name: "John Smith",
      email: "john.smith@cleri.com",
      lastSync: "1 hour ago",
      status: "synced",
      payrollData: {
        salary: "$65,000",
        payType: "Salary",
        taxInfo: "W-2"
      },
      operationalData: {
        jobRole: "Project Manager",
        crew: "Crew A",
        schedule: "Full Time"
      }
    },
    {
      id: 2,
      name: "Maria Garcia",
      email: "maria.garcia@cleri.com",
      lastSync: "3 hours ago",
      status: "pending",
      hasConflict: true, // Add conflict flag instead of status
      payrollData: {
        salary: "$65,000",
        payType: "Salary",
        taxInfo: "W-2"
      },
      operationalData: {
        jobRole: "Site Supervisor",
        crew: "Crew B",
        schedule: "Full Time"
      },
      conflictData: {
        quickbooks: {
          salary: "$47,000 (Updated 2 hours ago)",
          payType: "Hourly",
          taxInfo: "W-2"
        },
        cleri: {
          jobRole: "Senior Supervisor (Updated 1 hour ago)",
          crew: "Crew A",
          schedule: "Full-time"
        }
      }
    },
    {
      id: 3,
      name: "David Johnson",
      email: "david.johnson@cleri.com",
      lastSync: "30 minutes ago",
      status: "synced",
      payrollData: {
        salary: "$65,000",
        payType: "Salary",
        taxInfo: "W-2"
      },
      operationalData: {
        jobRole: "Equipment Operator",
        crew: "Crew A",
        schedule: "Part-time"
      }
    }
  ];

  const handlePageBack = () => {
    navigate("/quickbooks");
  };

  const handleResolveClick = (employee) => {
    setSelectedEmployee(employee);
    setResolveDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setResolveDialogOpen(false);
    setSelectedEmployee(null);
  };

  const handleUseQuickBooksData = () => {
    // Handle QuickBooks data selection
    console.log("Using QuickBooks data for:", selectedEmployee.name);
    handleCloseDialog();
  };

  const handleUseCleriData = () => {
    // Handle Cleri data selection
    console.log("Using Cleri data for:", selectedEmployee.name);
    handleCloseDialog();
  };

  return (
    <UserLayout
      contentHeightFixed={false}
      NavURL={handlePageBack}
      showIcon={true}
      activeLink="Quickbooks/Employees"
    >
      <Box sx={{ p: { xs: 1, md: 1 }, mx: "auto" }}>
        {/* Main Card */}
        <Card sx={{ overflow: "hidden" }}>
          <CardContent sx={{ p: 4 }}>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
              
              {/* Sync Now Button and Toggle */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
                <Typography variant="body1">Two-way Sync</Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={syncEnabled}
                      onChange={(e) => setSyncEnabled(e.target.checked)}
                    />
                    <span className="slider round"></span>
                  </label>
                </Box>
                
                <Button
                  variant="contained"
                  color="main"
                  size="medium"
                  startIcon={
                    <Icon icon="heroicons:arrow-path" width={20} height={20} />
                  }
                  disabled={!syncEnabled}
                >
                  Sync Now
                </Button>
              </Box>
            </Box>

            {/* Top Cards Section */}
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={3}>
                {topCardsData.map((card) => (
                  <Grid item xs={12} sm={6} md={3} key={card.id}>
                    <Box
                      sx={{
                        backgroundColor: "background.paper",
                        p: 4,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        minHeight: 140,
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          backgroundColor: card.bgColor,
                          borderRadius: "12px",
                          width: 48,
                          height: 48,
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon
                          icon={card.icon}
                          width={24}
                          height={24}
                          style={{ color: card.iconColor }}
                        />
                      </Box>

                      <Box sx={{ textAlign: "right", flex: 1, ml: 2 }}>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 700, mb: 1 }}
                        >
                          {card.value}
                        </Typography>
                        <Typography variant="body1">
                          {card.label}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Search and Filter Section */}
            <Card 
              sx={{ 
                borderRadius: 2, 
                border: "1px solid", 
                borderColor: "divider",
                backgroundColor: "background.paper",
                p: 4,
                mb: 4
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                  <Box sx={{ mb: 4 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Employee Synchronization
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Two-way sync: Payroll data → QuickBooks, Operational data → Cleri
              </Typography>
              </Box>
                
                <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                  <CustomTextField
                    placeholder="Search"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ 
                      width: 220,
                      '& .MuiOutlinedInput-root': {
                        height: '36px'
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon icon="tabler:search" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>

              {/* Employees List */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 3 }}>
                {employees.map((employee) => (
                  <Card
                    key={employee.id}
                    sx={{
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.paper",
                      overflow: "visible",
                    }}
                  >
                    <CardContent sx={{ px: 3 }}>
                      {/* Employee Header */}
                      <Box sx={{ px: 3 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0 }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 1, flexWrap: "wrap" }}>
                              <Typography variant="h5" sx={{ fontWeight: 650, color: "text.primary",mr: 8 }}>
                                {employee.name}
                              </Typography>
                              {/* Two way chip beside employee name */}
                              <Chip
                                label="Two way"
                                size="small"
                                variant="outlined"
                                sx={{
                                  backgroundColor: "#ffffff",
                                  color: "#525866",
                                  borderColor: "#525866",
                                  borderRadius: 1,
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                  height: 24,
                                  px: 2.5,
                                }}
                              />
                              {/* Conflict chip - only show if hasConflict is true */}
                              {employee.hasConflict && (
                                <Chip
                                  label="Conflict"
                                  size="small"
                                  sx={{
                                    backgroundColor: "#DA12120A",
                                    color: "#DA1212",
                                    fontWeight: 600,
                                    borderRadius: 1,
                                    fontSize: "0.75rem",
                                    height: 24,
                                    px: 1.5,
                                  }}
                                />
                              )}
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                              <Typography 
                                variant="body1" 
                                component="a" 
                                href={`mailto:${employee.email}`}
                                sx={{ 
                                  color: "primary.main",
                                  textDecoration: "none",
                                  '&:hover': {
                                    textDecoration: "underline"
                                  }
                                }}
                              >
                                {employee.email}
                              </Typography>
                              <Typography variant="body1" color="text.secondary">
                                Last Sync: {employee.lastSync}
                              </Typography>
                            </Box>
                          </Box>
                          
                          {/* Status and Resolve Button */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                             {employee.status === "pending" && employee.hasConflict && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleResolveClick(employee)}
                                 startIcon={
                               <Icon icon="ci:check-all" width={20} height={20} style={{ color: "#DD992F" }} />
                                            }
                                sx={{
                                  color: "#DD992F",
                                  borderColor: "#DD992F",
                                  fontWeight: 600,
                                  fontSize: "0.77rem",
                                  height: 32,
                                  minWidth: "auto",
                                  px: 2,
                                  '&:hover': {
                                    borderColor: "#DD992F80",
                                    backgroundColor: "#DD992F0A",
                                  }
                                }}
                              >
                                Resolve
                              </Button>
                            )}
                            <Chip
                              label={employee.status === "synced" ? "Synced" : "Pending"}
                              size="small"
                              sx={{
                                backgroundColor: 
                                  employee.status === "synced" ? "#E8F5E9" : "#FFF3E0",
                                color: 
                                  employee.status === "synced" ? "#23A26D" : "#DD992F",
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                height: 24,
                                px: 1.5,
                              }}
                            />
                            {/* Resolve button - only show for pending status with conflict */}
                           
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ p: 3 }}>
                        <Grid container spacing={3}>
                          {/* Payroll Data Section */}
                          <Grid item xs={12} md={6}>
                            <Box
                              sx={{
                                backgroundColor: "#003A700D",
                                borderRadius: 2,
                                p: 3,
                              }}
                            >
                              <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                  fontWeight: 600,
                                  color: "text.primary",
                                  mb: 3
                                }}
                              >
                                Payroll Data → QuickBooks
                              </Typography>
                              
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                                {/* Salary Row */}
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <Typography variant="body1" color="text.secondary">
                                    Salary
                                  </Typography>
                                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {employee.payrollData.salary}
                                  </Typography>
                                </Box>

                                {/* Pay Type Row */}
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <Typography variant="body1" color="text.secondary">
                                    Pay Type
                                  </Typography>
                                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {employee.payrollData.payType}
                                  </Typography>
                                </Box>

                                {/* Tax Info Row */}
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <Typography variant="body1" color="text.secondary">
                                    Tax Info
                                  </Typography>
                                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {employee.payrollData.taxInfo}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Grid>

                          {/* Operational Data Section */}
                          <Grid item xs={12} md={6}>
                            <Box
                              sx={{
                                backgroundColor: "#23A26D14",
                                borderRadius: 2,
                                p: 3,
                              }}
                            >
                              <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                  fontWeight: 600,
                                  color: "text.primary",
                                  mb: 3
                                }}
                              >
                                Operational Data → Cleri
                              </Typography>
                              
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                                {/* Job Role Row */}
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <Typography variant="body1" color="text.secondary">
                                    Job Role
                                  </Typography>
                                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {employee.operationalData.jobRole}
                                  </Typography>
                                </Box>

                                {/* Crew Row */}
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <Typography variant="body1" color="text.secondary">
                                    Crew
                                  </Typography>
                                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {employee.operationalData.crew}
                                  </Typography>
                                </Box>

                                {/* Schedule Row */}
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <Typography variant="body1" color="text.secondary">
                                    Schedule
                                  </Typography>
                                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {employee.operationalData.schedule}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Card>
          </CardContent>
        </Card>

        {/* Resolve Conflict Dialog */}
        <Dialog
          open={resolveDialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ pb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Resolve Sync Conflict
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Both systems have updates for {selectedEmployee?.name}. Choose which data to keep.
            </Typography>
          </DialogTitle>
          
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* QuickBooks Data Box */}
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 3,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  QuickBooks Data
                </Typography>
                
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                  {/* Salary Row */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body1" color="text.secondary">
                      Salary
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedEmployee?.conflictData?.quickbooks.salary}
                    </Typography>
                  </Box>

                  {/* Pay Type Row */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body1" color="text.secondary">
                      Pay Type
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedEmployee?.conflictData?.quickbooks.payType}
                    </Typography>
                  </Box>

                  {/* Tax Info Row */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body1" color="text.secondary">
                      Tax Info
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedEmployee?.conflictData?.quickbooks.taxInfo}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Cleri Data Box */}
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 3,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Cleri Data
                </Typography>
                
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                  {/* Job Role Row */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body1" color="text.secondary">
                      Job Role
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedEmployee?.conflictData?.cleri.jobRole}
                    </Typography>
                  </Box>

                  {/* Crew Row */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body1" color="text.secondary">
                      Crew
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedEmployee?.conflictData?.cleri.crew}
                    </Typography>
                  </Box>

                  {/* Schedule Row */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body1" color="text.secondary">
                      Schedule
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedEmployee?.conflictData?.cleri.schedule}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </DialogContent>

         <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'space-between' }}>
  <Button
    variant="contained"
    color= "info"
    onClick={handleCloseDialog}
  >
    Cancel
  </Button>
  <Box sx={{ display: 'flex', gap: 2 }}>
    <Button
      variant="outlined"
      onClick={handleUseQuickBooksData}
    >
      Use QuickBooks Data
    </Button>
    <Button
      variant="contained"
      color="main"
      onClick={handleUseCleriData}
    >
      Use Cleri Data
    </Button>
  </Box>
</DialogActions>
        </Dialog>
      </Box>
    </UserLayout>
  );
};

export default EmployeeSync;